import { create } from 'zustand';

/*
 * Gestor de subidas de Drive (2026-10-08), como el de MEGA: cada archivo es una «transferencia» con su progreso,
 * su velocidad y su tiempo restante, y la barra inferior (components/drive/BarraTransferencias.jsx, montada en
 * AppShell) las enseña aunque se cambie de Drive a Correo o al Calendario mientras sube.
 *
 * Uso:
 *   const ids = encolar(archivos, { destino: 'Mi unidad' });
 *   for (...) await ejecutar(id, (cfg) => api.post('/drive/files', fd, cfg), { alTerminar });
 * `cfg` lleva `signal` (cancelar) y `onUploadProgress` (axios). `ejecutar` devuelve la respuesta o lanza; un
 * archivo cancelado lanza un error con `cancelado: true`.
 *
 * La barra solo aparece para lo «pesado»: una tanda de 2 MB o más, de 4 archivos o más, o cualquier subida que
 * siga en marcha pasados 0,8 s. Lo pequeño se resuelve con el aviso de siempre y no parpadea nada.
 */

export const LIMITE_DRIVE = 512 * 1024 * 1024;   // = MAX_DRIVE_FILE del backend
const UMBRAL_LOTE = 2 * 1024 * 1024;
const RETRASO_VISIBLE = 800;
const VENTANA_VELOCIDAD = 4000;                  // ms de muestras para la velocidad

const ACTIVOS = new Set(['en_cola', 'subiendo', 'procesando']);
export const esActiva = (t) => ACTIVOS.has(t.estado);

// Lo que no se pinta vive fuera del estado de React: controladores, tareas y muestras de velocidad
const controladores = new Map();
const tareas = new Map();
const muestras = new Map();
const ultimoPintado = new Map();

let siguiente = 1;

export const useTransferencias = create(() => ({
  items: [],        // {id, lote, nombre, tamano, cargado, estado, error, destino, inicio, fin, velocidad, visible}
  abierta: false,   // lista desplegada
}));

const set = useTransferencias.setState;
const get = useTransferencias.getState;

function parchear(id, cambios) {
  set((s) => ({ items: s.items.map((t) => (t.id === id ? { ...t, ...cambios } : t)) }));
}

function mostrarLote(lote) {
  set((s) => ({ items: s.items.map((t) => (t.lote === lote && !t.visible ? { ...t, visible: true } : t)) }));
}

/** Pone los archivos en la cola y devuelve sus ids (en el mismo orden). */
export function encolar(archivos, { destino } = {}) {
  const lote = `l${siguiente++}`;
  const lista = Array.from(archivos || []);
  const total = lista.reduce((n, f) => n + (f.size || 0), 0);
  const visible = total >= UMBRAL_LOTE || lista.length >= 4;
  const nuevos = lista.map((f) => ({
    id: `t${siguiente++}`, lote, nombre: f.name || 'archivo', tamano: f.size || 0, cargado: 0,
    estado: 'en_cola', error: null, destino: destino || '', inicio: null, fin: null, velocidad: 0, visible,
  }));
  // Lo terminado de tandas anteriores se va al empezar otra (si no tiene errores que leer)
  set((s) => ({ items: [...s.items.filter((t) => esActiva(t) || t.estado === 'error'), ...nuevos] }));
  return nuevos.map((t) => t.id);
}

function velocidadDe(id, ahora, cargado) {
  const m = (muestras.get(id) || []).filter(([t]) => ahora - t <= VENTANA_VELOCIDAD);
  m.push([ahora, cargado]);
  muestras.set(id, m);
  if (m.length < 2) return 0;
  const [t0, c0] = m[0];
  const dt = (ahora - t0) / 1000;
  return dt > 0.25 ? Math.max(0, (cargado - c0) / dt) : 0;
}

function errorCancelado() {
  const e = new Error('Subida cancelada');
  e.cancelado = true;
  return e;
}

/** Sube UNA transferencia. `tarea(cfg)` hace la petición; `alTerminar` se llama al acabar (también al reintentar). */
export async function ejecutar(id, tarea, { alTerminar } = {}) {
  const t = get().items.find((x) => x.id === id);
  if (!t || t.estado === 'cancelado') throw errorCancelado();
  tareas.set(id, { tarea, alTerminar });
  if (t.tamano > LIMITE_DRIVE) {
    const motivo = 'Pesa más de 512 MB, el máximo por archivo';
    parchear(id, { estado: 'error', error: motivo, fin: Date.now(), visible: true });
    const e = new Error(motivo);
    e.response = { data: { detail: motivo } };
    alTerminar?.();
    throw e;
  }
  const control = new AbortController();
  controladores.set(id, control);
  muestras.delete(id);
  parchear(id, { estado: 'subiendo', cargado: 0, inicio: Date.now(), fin: null, error: null, velocidad: 0 });
  const reloj = setTimeout(() => {
    const x = get().items.find((y) => y.id === id);
    if (x && esActiva(x)) mostrarLote(x.lote);
  }, RETRASO_VISIBLE);

  const onUploadProgress = (ev) => {
    const ahora = Date.now();
    const total = ev.total || t.tamano || 0;
    const cargado = Math.min(ev.loaded || 0, total || ev.loaded || 0);
    const velocidad = velocidadDe(id, ahora, cargado);
    const acabado = total && cargado >= total;
    // axios avisa muchas veces por segundo: se pinta como mucho cada 150 ms (y siempre el final)
    if (!acabado && ahora - (ultimoPintado.get(id) || 0) < 150) return;
    ultimoPintado.set(id, ahora);
    parchear(id, { cargado, velocidad, ...(acabado ? { estado: 'procesando' } : {}) });
  };

  try {
    const r = await tarea({ signal: control.signal, onUploadProgress });
    parchear(id, { estado: 'listo', cargado: t.tamano, fin: Date.now(), velocidad: 0 });
    return r;
  } catch (err) {
    if (control.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
      parchear(id, { estado: 'cancelado', fin: Date.now(), velocidad: 0 });
      throw errorCancelado();
    }
    const motivo = err?.response?.data?.detail;
    parchear(id, {
      estado: 'error', fin: Date.now(), velocidad: 0, visible: true,
      error: typeof motivo === 'string' ? motivo : err?.response ? 'El servidor no aceptó el archivo' : 'Se cortó la conexión',
    });
    throw err;
  } finally {
    clearTimeout(reloj);
    controladores.delete(id);
    muestras.delete(id);
    ultimoPintado.delete(id);
    alTerminar?.();
  }
}

/** Cancela una transferencia: si está en cola ya no sale; si está subiendo se corta. */
export function cancelar(id) {
  const t = get().items.find((x) => x.id === id);
  if (!t || !esActiva(t)) return;
  if (t.estado === 'en_cola') parchear(id, { estado: 'cancelado', fin: Date.now() });
  controladores.get(id)?.abort();
}

export function cancelarTodo() {
  get().items.filter(esActiva).forEach((t) => cancelar(t.id));
}

/** Vuelve a subir una que falló o se canceló (con la misma petición y el mismo archivo). */
export async function reintentar(id) {
  const guardada = tareas.get(id);
  const t = get().items.find((x) => x.id === id);
  if (!guardada || !t || esActiva(t)) return;
  parchear(id, { estado: 'en_cola', error: null, cargado: 0 });
  try {
    await ejecutar(id, guardada.tarea, { alTerminar: guardada.alTerminar });
  } catch (_) { /* el estado ya lo cuenta en la barra */ }
}

/** Quita de la barra lo que ya acabó (listo, cancelado o con error). */
export function limpiarTerminadas() {
  set((s) => {
    const items = s.items.filter(esActiva);
    s.items.filter((t) => !esActiva(t)).forEach((t) => tareas.delete(t.id));
    return { items, abierta: items.length ? s.abierta : false };
  });
}

export const setAbierta = (abierta) => set({ abierta });

/** ¿Alguna de estas llegó a enseñarse en la barra? (si no, basta el aviso de siempre) */
export const seMostro = (ids) => get().items.some((t) => ids.includes(t.id) && t.visible);
